/**
 * Workshop Memory Service
 *
 * Temporal intelligence layer that converts raw documentation
 * into coherent, queryable history of work.
 *
 * This is where Ventura Machina becomes infrastructure.
 */

import type { ImageMetadata, ComponentCategory, RestorePhase, ConditionState } from '@/types';
import type {
  TemporalEvent,
  EventType,
  ProofTrail,
  ProofStep,
  MemorySummary,
  TimelineSegment,
  TimelineConfig,
  MemoryAnchor,
  ChangeDetection,
  WorkIntensity,
} from '@/types/memory';

/**
 * Generate a speakable evidence ID (A12, B07, etc.)
 * Large, easy to say aloud in a workshop
 */
export function generateEvidenceId(index: number): string {
  const letter = String.fromCharCode(65 + Math.floor(index / 100) % 26);
  const number = String(index % 100).padStart(2, '0');
  return `${letter}${number}`;
}

/**
 * Parse an evidence ID back to index
 */
export function parseEvidenceId(id: string): number {
  const letter = id.charCodeAt(0) - 65;
  const number = parseInt(id.slice(1), 10);
  return letter * 100 + number;
}

/**
 * Detect event type from image metadata
 */
export function detectEventType(image: ImageMetadata): EventType {
  const condition = image.condition;
  const phase = image.phase;
  const tags = image.tags.map(t => t.toLowerCase());

  // Check for discovery indicators
  if (tags.some(t => t.includes('rust') || t.includes('damage') || t.includes('issue'))) {
    return 'discovery';
  }

  // Map condition/phase to event type
  if (condition === 'as-received' || phase === 'initial-assessment') {
    return 'inspection';
  }
  if (condition === 'disassembly' || phase === 'teardown') {
    return 'teardown';
  }
  if (tags.some(t => t.includes('weld') || t.includes('fabricat') || t.includes('metal work'))) {
    return 'fabrication';
  }
  if (condition === 'repaired' || phase === 'restoration') {
    return 'repair';
  }
  if (condition === 'installed' || phase === 'assembly') {
    return 'installation';
  }
  if (condition === 'painted' || condition === 'completed' || phase === 'finishing') {
    return 'finishing';
  }

  return 'documentation';
}

/**
 * Determine work intensity from images in a time window
 */
export function calculateIntensity(imageCount: number, hoursSpan: number): WorkIntensity {
  const rate = imageCount / Math.max(hoursSpan, 1);
  if (rate > 5) return 'heavy';
  if (rate > 2) return 'moderate';
  return 'light';
}

/**
 * Cluster images into temporal events
 *
 * Groups images taken within time windows by component similarity
 */
export function clusterIntoEvents(
  images: ImageMetadata[],
  options: {
    timeWindowHours?: number;
    minImagesPerEvent?: number;
  } = {}
): TemporalEvent[] {
  const { timeWindowHours = 4, minImagesPerEvent = 2 } = options;

  if (images.length === 0) return [];

  // Sort by capture time
  const sorted = [...images].sort(
    (a, b) => new Date(a.capturedAt).getTime() - new Date(b.capturedAt).getTime()
  );

  const events: TemporalEvent[] = [];
  let currentCluster: ImageMetadata[] = [sorted[0]];
  let clusterStart = new Date(sorted[0].capturedAt);

  for (let i = 1; i < sorted.length; i++) {
    const img = sorted[i];
    const imgTime = new Date(img.capturedAt);
    const hoursDiff = (imgTime.getTime() - clusterStart.getTime()) / (1000 * 60 * 60);

    // Check if same cluster (within time window and similar component)
    const sameComponent = currentCluster.some(c => c.component === img.component);
    const withinWindow = hoursDiff <= timeWindowHours;

    if (withinWindow && sameComponent) {
      currentCluster.push(img);
    } else {
      // Create event from current cluster
      if (currentCluster.length >= minImagesPerEvent) {
        events.push(createEventFromCluster(currentCluster, events.length));
      }
      // Start new cluster
      currentCluster = [img];
      clusterStart = imgTime;
    }
  }

  // Don't forget the last cluster
  if (currentCluster.length >= minImagesPerEvent) {
    events.push(createEventFromCluster(currentCluster, events.length));
  }

  // Link events
  for (let i = 0; i < events.length; i++) {
    if (i > 0) events[i].previousEventId = events[i - 1].id;
    if (i < events.length - 1) events[i].nextEventId = events[i + 1].id;
  }

  return events;
}

/**
 * Create a temporal event from a cluster of images
 */
function createEventFromCluster(images: ImageMetadata[], index: number): TemporalEvent {
  const startTime = images.reduce(
    (min, img) => (img.capturedAt < min ? img.capturedAt : min),
    images[0].capturedAt
  );
  const endTime = images.reduce(
    (max, img) => (img.capturedAt > max ? img.capturedAt : max),
    images[0].capturedAt
  );

  const components = [...new Set(images.map(i => i.component))];
  const componentDetails = [...new Set(images.map(i => i.componentDetail))];
  const phases = [...new Set(images.map(i => i.phase))];
  const eventType = detectEventType(images[0]);

  const duration = (new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60 * 60);
  const intensity = calculateIntensity(images.length, duration || 1);

  // Find best representative image (highest confidence hero, or highest confidence)
  const heroImages = images.filter(i => i.isHeroImage);
  const primaryImage = heroImages.length > 0
    ? heroImages.reduce((best, img) => img.confidence > best.confidence ? img : best)
    : images.reduce((best, img) => img.confidence > best.confidence ? img : best);

  // Generate title
  const title = generateEventTitle(eventType, componentDetails, images[0].condition);

  return {
    id: `evt-${images[0].vehicleId}-${index.toString().padStart(3, '0')}`,
    vehicleId: images[0].vehicleId,
    type: eventType,
    title,
    startTime,
    endTime: startTime !== endTime ? endTime : undefined,
    duration: duration > 0 ? duration : undefined,
    components,
    componentDetails,
    phase: phases[0],
    imageIds: images.map(i => i.id),
    primaryImageId: primaryImage.id,
    imageCount: images.length,
    confidence: Math.round(images.reduce((sum, i) => sum + i.confidence, 0) / images.length),
    autoGenerated: true,
    intensity,
    relatedEventIds: [],
    isAnchor: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Generate a human-readable event title
 */
function generateEventTitle(
  eventType: EventType,
  componentDetails: string[],
  condition: ConditionState
): string {
  const component = componentDetails[0] || 'component';

  const typeLabels: Record<EventType, string> = {
    inspection: 'Initial Assessment',
    teardown: 'Teardown',
    fabrication: 'Fabrication',
    repair: 'Repair Work',
    installation: 'Installation',
    finishing: 'Finishing',
    milestone: 'Milestone',
    discovery: 'Discovery',
    documentation: 'Documentation',
  };

  return `${component} - ${typeLabels[eventType]}`;
}

/**
 * Build a proof trail for a component
 *
 * Creates: Before → Removal → Fabrication → Installation → Final
 */
export function buildProofTrail(
  events: TemporalEvent[],
  images: ImageMetadata[],
  componentDetail: string
): ProofTrail | null {
  // Find events related to this component
  const relatedEvents = events.filter(e =>
    e.componentDetails.some(c => c.toLowerCase().includes(componentDetail.toLowerCase()))
  );

  if (relatedEvents.length === 0) return null;

  // Sort by time
  relatedEvents.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  // Find related images
  const relatedImages = images.filter(i =>
    i.componentDetail.toLowerCase().includes(componentDetail.toLowerCase())
  );

  // Build proof steps based on condition progression
  const steps: ProofStep[] = [];
  const conditionOrder: ConditionState[] = [
    'as-received',
    'disassembly',
    'stripped',
    'repaired',
    'installed',
    'completed',
  ];

  const stepLabels: Record<string, string> = {
    'as-received': 'Before',
    'disassembly': 'Removal',
    'stripped': 'Stripped',
    'repaired': 'Fabrication/Repair',
    'installed': 'Installation',
    'completed': 'Final',
  };

  for (const condition of conditionOrder) {
    const conditionImages = relatedImages.filter(i => i.condition === condition);
    if (conditionImages.length === 0) continue;

    const event = relatedEvents.find(e =>
      e.imageIds.some(id => conditionImages.some(ci => ci.id === id))
    );

    const bestImage = conditionImages.reduce((best, img) =>
      img.confidence > best.confidence ? img : best
    );

    steps.push({
      id: `step-${steps.length}`,
      order: steps.length,
      label: stepLabels[condition] || condition,
      description: `${componentDetail} - ${condition}`,
      condition,
      eventId: event?.id,
      imageIds: conditionImages.map(i => i.id),
      primaryImageId: bestImage.id,
      timestamp: bestImage.capturedAt,
      hasEvidence: true,
      confidence: Math.round(
        conditionImages.reduce((sum, i) => sum + i.confidence, 0) / conditionImages.length
      ),
    });
  }

  if (steps.length < 2) return null;

  const firstEvent = relatedEvents[0];
  const lastEvent = relatedEvents[relatedEvents.length - 1];

  return {
    id: `proof-${firstEvent.vehicleId}-${componentDetail.replace(/\s+/g, '-').toLowerCase()}`,
    vehicleId: firstEvent.vehicleId,
    title: `${componentDetail} Replacement`,
    component: componentDetail,
    componentCategory: firstEvent.components[0],
    steps,
    isComplete: steps.length >= 3,
    confidence: Math.round(steps.reduce((sum, s) => sum + s.confidence, 0) / steps.length),
    totalImages: steps.reduce((sum, s) => sum + s.imageIds.length, 0),
    dateRange: {
      start: firstEvent.startTime,
      end: lastEvent.endTime || lastEvent.startTime,
    },
    exportable: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Generate a memory summary for a time period
 */
export function generateSummary(
  events: TemporalEvent[],
  periodStart: string,
  periodEnd: string
): MemorySummary | null {
  const periodEvents = events.filter(e => {
    const eventTime = new Date(e.startTime);
    return eventTime >= new Date(periodStart) && eventTime <= new Date(periodEnd);
  });

  if (periodEvents.length === 0) return null;

  // Aggregate data
  const components = [...new Set(periodEvents.flatMap(e => e.components))];
  const phases = [...new Set(periodEvents.map(e => e.phase))];
  const eventTypes = [...new Set(periodEvents.map(e => e.type))];
  const totalImages = periodEvents.reduce((sum, e) => sum + e.imageCount, 0);

  // Generate bullet points
  const bulletPoints: string[] = [];
  if (eventTypes.includes('discovery')) {
    bulletPoints.push('Discoveries and issues identified');
  }
  if (eventTypes.includes('teardown')) {
    bulletPoints.push('Teardown and disassembly work');
  }
  if (eventTypes.includes('fabrication')) {
    bulletPoints.push('Fabrication and metal work');
  }
  if (eventTypes.includes('installation')) {
    bulletPoints.push('Assembly and installation');
  }
  if (eventTypes.includes('finishing')) {
    bulletPoints.push('Finishing and detail work');
  }

  // Calculate intensity
  const durationHours = (new Date(periodEnd).getTime() - new Date(periodStart).getTime()) / (1000 * 60 * 60);
  const intensity = calculateIntensity(totalImages, durationHours);

  // Generate period label
  const startDate = new Date(periodStart);
  const periodLabel = startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Generate title based on primary activity
  const primaryPhase = phases[0];
  const phaseLabels: Record<RestorePhase, string> = {
    'initial-assessment': 'Initial Assessment',
    'teardown': 'Teardown Phase',
    'inventory': 'Inventory & Parts',
    'restoration': 'Restoration Work',
    'assembly': 'Assembly Phase',
    'finishing': 'Finishing Phase',
    'quality-check': 'Quality Check',
    'delivery': 'Delivery',
    'completed': 'Project Completed',
  };

  // Find hero images
  const heroImageIds = periodEvents
    .filter(e => e.primaryImageId)
    .slice(0, 3)
    .map(e => e.primaryImageId!);

  return {
    id: `summary-${periodStart.slice(0, 7)}`,
    vehicleId: periodEvents[0].vehicleId,
    periodStart,
    periodEnd,
    periodLabel,
    title: `${phaseLabels[primaryPhase] || 'Work'} - ${periodLabel}`,
    narrative: `${periodLabel} focused on ${components.join(', ')} work. ${periodEvents.length} events documented with ${totalImages} images.`,
    bulletPoints,
    eventCount: periodEvents.length,
    eventIds: periodEvents.map(e => e.id),
    primaryComponents: components,
    phases,
    heroImageIds,
    intensity,
    imageCount: totalImages,
    autoGenerated: true,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Build timeline segments for visualization
 */
export function buildTimelineSegments(
  events: TemporalEvent[],
  config: TimelineConfig
): TimelineSegment[] {
  if (events.length === 0) return [];

  // Sort events by time
  const sorted = [...events].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  const segments: TimelineSegment[] = [];

  // Group events by time period based on scale
  const periodMs = {
    day: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000,
    quarter: 90 * 24 * 60 * 60 * 1000,
    year: 365 * 24 * 60 * 60 * 1000,
  }[config.scale];

  let currentPeriodStart = new Date(sorted[0].startTime);
  let currentPeriodEvents: TemporalEvent[] = [];

  for (const event of sorted) {
    const eventTime = new Date(event.startTime);
    const periodDiff = eventTime.getTime() - currentPeriodStart.getTime();

    if (periodDiff > periodMs) {
      // Check for gap
      if (config.showGaps && periodDiff > periodMs * 1.5 && currentPeriodEvents.length > 0) {
        // Create gap segment
        const gapStart = new Date(currentPeriodStart.getTime() + periodMs);
        segments.push({
          id: `gap-${segments.length}`,
          vehicleId: event.vehicleId,
          startTime: gapStart.toISOString(),
          endTime: new Date(eventTime.getTime() - periodMs).toISOString(),
          label: 'No activity',
          color: '#374151',
          height: 0.1,
          eventIds: [],
          eventCount: 0,
          imageCount: 0,
          primaryPhase: 'initial-assessment',
          components: [],
          isGap: true,
        });
      }

      // Create segment from current period
      if (currentPeriodEvents.length > 0) {
        segments.push(createSegmentFromEvents(currentPeriodEvents, segments.length));
      }

      // Start new period
      currentPeriodStart = eventTime;
      currentPeriodEvents = [event];
    } else {
      currentPeriodEvents.push(event);
    }
  }

  // Don't forget the last period
  if (currentPeriodEvents.length > 0) {
    segments.push(createSegmentFromEvents(currentPeriodEvents, segments.length));
  }

  return segments;
}

/**
 * Create a timeline segment from events
 */
function createSegmentFromEvents(events: TemporalEvent[], index: number): TimelineSegment {
  const startTime = events.reduce(
    (min, e) => (e.startTime < min ? e.startTime : min),
    events[0].startTime
  );
  const endTime = events.reduce(
    (max, e) => {
      const t = e.endTime || e.startTime;
      return t > max ? t : max;
    },
    events[0].endTime || events[0].startTime
  );

  const components = [...new Set(events.flatMap(e => e.components))];
  const primaryPhase = events[0].phase;
  const imageCount = events.reduce((sum, e) => sum + e.imageCount, 0);

  // Color based on phase
  const phaseColors: Record<RestorePhase, string> = {
    'initial-assessment': '#6366F1',
    'teardown': '#EF4444',
    'inventory': '#F59E0B',
    'restoration': '#10B981',
    'assembly': '#3B82F6',
    'finishing': '#8B5CF6',
    'quality-check': '#EC4899',
    'delivery': '#14B8A6',
    'completed': '#22C55E',
  };

  // Height based on activity level
  const avgIntensity = events.reduce((sum, e) => {
    const intensityValue = { light: 0.3, moderate: 0.6, heavy: 1.0 }[e.intensity];
    return sum + intensityValue;
  }, 0) / events.length;

  return {
    id: `seg-${index}`,
    vehicleId: events[0].vehicleId,
    startTime,
    endTime,
    label: events[0].title,
    color: phaseColors[primaryPhase] || '#6B7280',
    height: avgIntensity,
    eventIds: events.map(e => e.id),
    eventCount: events.length,
    imageCount,
    primaryPhase,
    components,
    thumbnailUrl: events[0].primaryImageId ? undefined : undefined, // Would resolve to image URL
    summary: `${events.length} events, ${imageCount} images`,
    isGap: false,
  };
}

/**
 * Detect changes over time for a component
 */
export function detectChanges(
  events: TemporalEvent[],
  images: ImageMetadata[],
  component: ComponentCategory
): ChangeDetection[] {
  const componentEvents = events.filter(e => e.components.includes(component));
  const componentImages = images.filter(i => i.component === component);

  if (componentEvents.length < 2) return [];

  const changes: ChangeDetection[] = [];
  const detailGroups = new Map<string, ImageMetadata[]>();

  // Group by component detail
  for (const img of componentImages) {
    const detail = img.componentDetail.toLowerCase();
    if (!detailGroups.has(detail)) {
      detailGroups.set(detail, []);
    }
    detailGroups.get(detail)!.push(img);
  }

  // Analyze each detail group for changes
  for (const [detail, imgs] of detailGroups) {
    const sorted = imgs.sort(
      (a, b) => new Date(a.capturedAt).getTime() - new Date(b.capturedAt).getTime()
    );

    // Look for condition changes
    const conditions = sorted.map(i => i.condition);
    const uniqueConditions = [...new Set(conditions)];

    if (uniqueConditions.length > 1) {
      // Significant change detected
      const firstImg = sorted[0];
      const lastImg = sorted[sorted.length - 1];

      const changeType = determineChangeType(firstImg.condition, lastImg.condition);

      changes.push({
        componentDetail: detail,
        component,
        changeType,
        changeIntensity: calculateIntensity(sorted.length, 24),
        detectedAt: lastImg.capturedAt,
        firstSeenAt: firstImg.capturedAt,
        lastSeenAt: lastImg.capturedAt,
        beforeImageId: firstImg.id,
        afterImageId: lastImg.id,
        relatedEventIds: componentEvents
          .filter(e => e.imageIds.some(id => sorted.some(i => i.id === id)))
          .map(e => e.id),
        confidence: Math.round((firstImg.confidence + lastImg.confidence) / 2),
      });
    }
  }

  return changes;
}

/**
 * Determine type of change between conditions
 */
function determineChangeType(
  before: ConditionState,
  after: ConditionState
): 'appeared' | 'disappeared' | 'modified' | 'replaced' {
  if (before === 'as-received' && after === 'completed') return 'replaced';
  if (after === 'completed' || after === 'installed') return 'modified';
  return 'modified';
}

/**
 * Find events that overlap in time with a target
 */
export function findOverlappingEvents(
  events: TemporalEvent[],
  targetComponent: ComponentCategory
): TemporalEvent[] {
  const targetEvents = events.filter(e => e.components.includes(targetComponent));
  if (targetEvents.length === 0) return [];

  // Get time range of target
  const targetStart = Math.min(...targetEvents.map(e => new Date(e.startTime).getTime()));
  const targetEnd = Math.max(...targetEvents.map(e =>
    new Date(e.endTime || e.startTime).getTime()
  ));

  // Find other events that overlap
  return events.filter(e => {
    if (e.components.includes(targetComponent)) return false;

    const eventStart = new Date(e.startTime).getTime();
    const eventEnd = new Date(e.endTime || e.startTime).getTime();

    return eventStart <= targetEnd && eventEnd >= targetStart;
  });
}

/**
 * Parse natural language temporal query
 */
export function parseTemporalQuery(query: string): {
  timeReference?: { relative?: string; month?: string; year?: string };
  component?: string;
  action?: 'when' | 'what' | 'prove' | 'show' | 'overlap';
} {
  const lowerQuery = query.toLowerCase();

  // Time references
  const months = ['january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'];
  const relativeTerms = ['last week', 'last month', 'this month', 'early', 'late'];

  let timeReference: { relative?: string; month?: string; year?: string } | undefined;

  for (const month of months) {
    if (lowerQuery.includes(month)) {
      timeReference = { month };
      break;
    }
  }

  for (const term of relativeTerms) {
    if (lowerQuery.includes(term)) {
      timeReference = { ...timeReference, relative: term };
      break;
    }
  }

  // Action detection
  let action: 'when' | 'what' | 'prove' | 'show' | 'overlap' | undefined;
  if (lowerQuery.includes('when')) action = 'when';
  else if (lowerQuery.includes('what')) action = 'what';
  else if (lowerQuery.includes('prove')) action = 'prove';
  else if (lowerQuery.includes('overlap')) action = 'overlap';
  else action = 'show';

  return { timeReference, action };
}

/**
 * Workshop Memory API object
 */
export const workshopMemory = {
  generateEvidenceId,
  parseEvidenceId,
  detectEventType,
  calculateIntensity,
  clusterIntoEvents,
  buildProofTrail,
  generateSummary,
  buildTimelineSegments,
  detectChanges,
  findOverlappingEvents,
  parseTemporalQuery,
};

export default workshopMemory;
